/**
 * Sales Order Create Page.
 *
 * Form for creating a new sales order. Select a customer and optionally
 * set a commission rate. Line items are added from the detail page.
 */

import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesDialog from "@/components/ui/UnsavedChangesDialog";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useCustomers, useCreateSalesOrder } from "@/features/sales/api";
import { toast } from "@/stores/toastStore";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import Button from "@/components/ui/Button";
import ComboSelect from "@/components/ui/ComboSelect";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";

interface Salesperson {
  id: string;
  full_name: string;
  default_commission_rate: number | null;
}

function SalesOrderCreatePage() {
  const navigate = useNavigate();
  const skipBlockRef = useRef(false);
  const { user } = useAuth();
  const { data: customerData } = useCustomers();
  const { data: salespeopleData } = useQuery<{ items: Salesperson[] }>({
    queryKey: ["users", "salespeople"],
    queryFn: async () =>
      (
        await apiClient.get(
          "/api/v1/users?can_sell=true&is_active=true&page_size=100",
        )
      ).data,
  });
  const createMutation = useCreateSalesOrder();

  const salespeople = salespeopleData?.items || [];
  const { hasPermission } = usePermissions();
  const canOverrideCommission = hasPermission("sales.override_commission");

  // Default to current user if they can sell, otherwise empty.
  const defaultSalesperson =
    user && salespeople.find((s) => s.id === user.id) ? user.id : "";

  const [customerId, setCustomerId] = useState("");
  const [salespersonId, setSalespersonId] =
    useState<string>(defaultSalesperson);
  // Tax is engine-owned (computed from the org's schemes and the customer's
  // certificates) and delivery money is now an itemised charge added on the order
  // detail page, so neither is an input here.
  const [discountType, setDiscountType] = useState<"" | "percent" | "amount">(
    "",
  );
  const [discountValue, setDiscountValue] = useState("");
  const [commissionRate, setCommissionRate] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  // --- Unsaved changes protection ---
  const isDirty =
    !skipBlockRef.current &&
    (customerId !== "" ||
      salespersonId !== "" ||
      commissionRate !== "" ||
      discountValue !== "" ||
      notes !== "");

  const { isBlocked, proceed, reset, checkUnsaved } =
    useUnsavedChanges(isDirty);

  const saveDraft = async () => {
    if (!customerId) {
      toast.error("Select a customer to save draft");
      return;
    }
    skipBlockRef.current = true;
    try {
      const so = await createMutation.mutateAsync({
        customer_id: customerId,
        salesperson_id: salespersonId || undefined,
        discount_type: discountType || undefined,
        discount_value: discountValue ? parseFloat(discountValue) : undefined,
        commission_rate: commissionRate
          ? parseFloat(commissionRate) / 100
          : undefined,
        notes: notes || undefined,
      });
      navigate(`/sales/${so.id}`);
    } catch {
      skipBlockRef.current = false;
      toast.error("Failed to save draft");
    }
  };

  // When salesperson changes, auto-fill commission from their default.
  const handleSalespersonChange = (id: string) => {
    setSalespersonId(id);
    const sp = salespeople.find((s) => s.id === id);
    if (sp?.default_commission_rate != null) {
      setCommissionRate((Number(sp.default_commission_rate) * 100).toFixed(2));
    } else {
      setCommissionRate("");
    }
  };

  // Auto-fill once salespeople load and we have a default.
  if (!salespersonId && defaultSalesperson && salespeople.length > 0) {
    handleSalespersonChange(defaultSalesperson);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!customerId) {
      setError("Please select a customer.");
      return;
    }

    try {
      const so = await createMutation.mutateAsync({
        customer_id: customerId,
        salesperson_id: salespersonId || undefined,
        discount_type: discountType || undefined,
        discount_value: discountValue ? parseFloat(discountValue) : undefined,
        commission_rate: commissionRate
          ? parseFloat(commissionRate) / 100
          : undefined,
        notes: notes || undefined,
      });
      skipBlockRef.current = true;
      navigate(`/sales/${so.id}`);
    } catch {
      setError("Failed to create sales order.");
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          New Sales Order
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Create a sale, then add assets from the detail page.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-[var(--radius-md)] bg-[var(--fill-danger)] p-3 text-sm text-[var(--text-danger)]">
            {error}
          </div>
        )}

        <Card>
          <ComboSelect
            label="Customer"
            options={(customerData?.items || []).map((c) => ({
              value: c.id,
              label: c.name,
              sublabel: c.company_name || undefined,
            }))}
            value={customerId}
            onChange={setCustomerId}
            placeholder="Select a customer..."
            required
          />
        </Card>

        <Card>
          <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
            Salesperson & Commission
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ComboSelect
              label="Salesperson"
              options={salespeople.map((sp) => ({
                value: sp.id,
                label: sp.full_name,
                sublabel:
                  sp.default_commission_rate != null
                    ? `${(Number(sp.default_commission_rate) * 100).toFixed(1)}% commission`
                    : undefined,
              }))}
              value={salespersonId}
              onChange={handleSalespersonChange}
              placeholder="Select salesperson..."
              clearable
            />
            <Input
              label="Commission (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              placeholder="5.00"
              disabled={!canOverrideCommission && !!salespersonId}
            />
          </div>
        </Card>

        <Card>
          <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
            Details
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">
                Order Discount
              </label>
              <select
                value={discountType}
                onChange={(e) =>
                  setDiscountType(e.target.value as "" | "percent" | "amount")
                }
                className="mt-1 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                <option value="">No discount</option>
                <option value="percent">Percentage</option>
                <option value="amount">Fixed amount</option>
              </select>
            </div>
            <Input
              label={
                discountType === "percent" ? "Discount (%)" : "Discount ($)"
              }
              type="number"
              step="0.01"
              min="0"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder="0.00"
              disabled={discountType === ""}
            />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-[var(--text-secondary)]">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional"
              className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm focus:border-[var(--border-focus)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
            />
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => checkUnsaved(() => navigate("/sales"))}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={saveDraft}
          >
            <Save className="h-3.5 w-3.5" /> Save Draft
          </Button>
          <Button type="submit" isLoading={createMutation.isPending}>
            Create Sales Order
          </Button>
        </div>
      </form>

      <UnsavedChangesDialog
        isOpen={isBlocked}
        onSaveDraft={saveDraft}
        onDiscard={proceed}
        onStay={reset}
        entityName="sales order"
      />
    </div>
  );
}

export default SalesOrderCreatePage;
